import React, { useEffect, useRef, useState } from "react";
import "./referralPhysicians.css";
import { physicianSubline } from "./physicianForm";
import { searchPhysicians } from "./referralPhysiciansApi";

// "Find a physician" in the Referrals picker (owner item 3, 2026-10-19): the
// chips show the 15 most used; this box searches every active physician (name,
// specialty, city, NPI). Picking one opens the same diagnosis step as a chip.
// When nothing matches, a user who may add physicians gets "Add '<typed>'".

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

export default function PhysicianSearch({ canAdd, onPick, onAddNew, debounceMs = 250 }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);
  const term = q.trim();

  useEffect(() => {
    if (term.length < 2) {
      seq.current += 1;
      setRows([]);
      setLoading(false);
      setFailed(false);
      return undefined;
    }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const found = await searchPhysicians(term, { limit: 15 });
        if (mine === seq.current) {
          setRows(found);
          setFailed(false);
        }
      } catch (e) {
        if (mine === seq.current) {
          setRows([]);
          setFailed(true);
        }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [term, debounceMs]);

  const pick = (row) => {
    setQ("");
    if (onPick) onPick(row);
  };

  const addNew = () => {
    const typed = term;
    setQ("");
    if (onAddNew) onAddNew(typed);
  };

  return (
    <div className="rp rp-search">
      <div className="rp-search-box">
        <SearchIcon />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find a physician (name, specialty, city, NPI)"
          aria-label="Find a referral physician"
          autoComplete="off"
        />
      </div>
      {term.length >= 2 && rows.length > 0 && (
        <ul className="rp-results">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" className="rp-result" onClick={() => pick(row)}>
                <span>
                  <span className="rp-result-name">{row.doctor_name}</span>
                  <span className="rp-result-sub">{physicianSubline(row)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {term.length >= 2 && !loading && rows.length === 0 && (
        failed ? (
          <p className="rp-hint" role="status">Search is not available right now.</p>
        ) : canAdd ? (
          <ul className="rp-results">
            <li>
              <button type="button" className="rp-result" onClick={addNew}>
                <span className="rp-result-add">+ Add “{term}” as a new physician</span>
              </button>
            </li>
          </ul>
        ) : (
          <p className="rp-hint" role="status">No physician found.</p>
        )
      )}
    </div>
  );
}
